import { css } from 'styled-components';
import { InputBackground, InputType } from './models';

export const InputOuterWrap = css(
  ({ theme }) => css`
    position: relative;
    border-width: 0px;
    flex-grow: 1;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    @media only screen and (min-width: 800px) {
      flex-basis: 0;
    }
  `
);

export const CurrentSymbol = css<{ isAnimated: boolean }>(
  ({ theme, isAnimated }) => css`
    position: absolute;
    padding-left: ${isAnimated ? '8px' : '20px'};
    left: 0;
    top: 50%;
    font-size: 16px;
    font-weight: 500;
    color: ${theme.colors.primaryBackground};
    transform: translateY(-50%);
  `
);

export const InputWrapper = css(
  ({ theme }) => css`
    position: relative;

    & label {
      background-color: #0e90d2;
      pointer-events: none;
    }

    & label::before {
      content: attr(title);
      position: absolute;
      top: 0;
      left: 30px;
      line-height: 40px;
      font-size: ${theme.font.size.smaller};
      color: #777;
      transition: 300ms all;
    }

    & input {
      width: 100%;
      box-sizing: border-box;
      font-size: ${theme.font.size.smallMedium};
      line-height: 28px;
      border-radius: 8px;
      overflow: scroll;

      &:focus {
        outline: 0;
        border-color: ${theme.colors.primaryBright};

        &[readonly] {
          border-color: transparent;
        }
      }

      &:valid + label::before,
      &:focus + label::before,
      &:not([value='']) ~ label::before {
        line-height: 20px;
        font-size: 12px;
        top: -10px;
        background: #fff;
        padding: 0 6px;
        left: 9px;
      }

      &:valid + label::before {
        content: attr(data-title);
      }

      &:focus + label::before {
        color: ${theme.colors.primaryBright};
      }

      &#error:focus {
        outline: 0;
        border-color: red;
      }

      &#error:focus + label::before {
        color: red;
      }
    }
  `
);

const inputBorder = css<{ error: boolean; focus: boolean; blue: boolean; warning: boolean }>(
  ({ error, focus, blue, warning, theme }) => {
    if (!!error) return css`1px solid ${theme.colors.error} !important`;
    if (!!warning) return css`1px solid ${theme.colors.warning} !important;`;
    if (!!focus) return css`2px solid ${theme.colors.primaryBright}`;
    if (!!blue) return `1px solid ${theme.colors.disabledBG}`;

    return `2px solid transparent`;
  }
);

export const InputInnerWrapper = css<{
  error: boolean;
  disabled: boolean;
  warning: boolean;
  background: InputBackground;
  focus?: boolean;
  blue?: boolean;
}>(
  ({ theme, error, disabled = false, background, focus, blue, warning }) =>
    css`
      display: flex;
      background-color: ${blue ? ' white' : background};
      border-radius: ${theme.borderRadiusCommon}px;
      position: relative;
      align-items: center;

      > input {
        border: ${inputBorder};
      }

      > label:before {
        color: ${!!error ? theme.colors.error + '!important' : 'inherit'};
      }

      ${disabled &&
      css`
        font-weight: 900;
        -webkit-text-fill-color: ${theme.colors.mediumGray};
      `};

      > .button-wrapper {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        height: fit-content;
        right: 0;
      }
    `
);

const inputBlueStyles = css(
  ({ theme }) => css`
    font-weight: ${theme.font.weight.bolder} !important;
    color: ${theme.colors.primaryBright} !important;
    background-color: white !important;

    ::placeholder,
    ::-webkit-input-placeholder {
      font-weight: ${theme.font.weight.bolder};
      color: ${theme.colors.primaryBright};
    }
  `
);

const inputSmallStyles =({ isAnimated, showCurrencySymbol }) => css`
  padding: ${isAnimated ? (showCurrencySymbol ? '1px 8px 1px 20px' : '1px 8px') : '1px 15px'} !important;
`;

const securedInputStyles = css`
  -webkit-text-security: disc;
  text-security: disc;
`;

const inputCustomButtonStyles = css<{ isAnimated: boolean, customButtonSizes: { width: number; height: number } }>(
  ({ theme, isAnimated, customButtonSizes }) => css`
    padding: ${isAnimated ? '8px ${customButtonSizes.width}px 8px 8px' : '0 ${customButtonSizes.width}px'} !important;

    min-height: ${customButtonSizes.height}px;
  `
);

export const Input = css<{
  blue: boolean;
  centeredText: boolean;
  small: boolean;
  isSecuredInput: boolean;
  pattern: string;
  customButtonSizes?: { width: number; height: number };
  isAnimated: boolean;
  showCurrencySymbol: boolean;
}>(({ theme, blue, centeredText, small, isSecuredInput, customButtonSizes, isAnimated, showCurrencySymbol }) => {
  return css`
    font-weight: ${theme.font.weight.medium};
    font-size: ${theme.font.size.smallMedium2};
    line-height: 22px;
    box-sizing: border-box;
    color: ${theme.colors.primaryBackground};
    background-color: transparent;
    appearance: none;
    outline: none;
    padding: ${isAnimated ? (showCurrencySymbol? '8px 8px 8px 20px' : '8px') : '${theme.spacing.smallMedium}px 15px'};
    width: 100%;
    text-align: ${centeredText ? 'center' : 'initial'};

    ${isSecuredInput && securedInputStyles};
    ${small && inputSmallStyles({ isAnimated, showCurrencySymbol })};
    ${!!customButtonSizes && inputCustomButtonStyles};

    &:invalid {
      box-shadow: none;
    }

    ::placeholder,
    ::-webkit-input-placeholder {
      font-weight: ${theme.font.weight.medium};
      color: ${theme.colors.placeholderGray};
      font-size: ${theme.font.size.smallMedium};
    }

    &:focus {
      ::placeholder {
        color: transparent;
      }
    }

    ${blue && inputBlueStyles};
  `;
});

export const ErrorMessage = css<{ initErrorSpacing: boolean }>(
  ({ theme, initErrorSpacing }) => css`
    font-weight: ${theme.font.weight.light};
    font-size: ${theme.font.size.smaller};
    margin-top: 5px;
    left: 7px;
    bottom: -20px;
    line-height: 10px;
    color: ${theme.colors.error};
    ${initErrorSpacing &&
    css`
      height: 18px;
    `};
  `
);

export const WarningMessage = css<{ initWarningSpacing: boolean }>(
  ({ theme, initWarningSpacing }) => css`
    font-weight: ${theme.font.weight.light};
    font-size: ${theme.font.size.smaller};
    margin-top: 5px;
    left: 7px;
    bottom: -20px;
    line-height: 15px;
    color: ${theme.colors.warning};
    max-width: 230px;
    text-align: center;
    ${initWarningSpacing &&
    css`
      height: 18px;
    `};
  `
);
